import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Radio, Checkbox, Upload, Button, Row, Col, Card, Divider, Space, message, Typography } from 'antd';
import { UploadOutlined, CalendarOutlined, UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import apiService from '../services/api';
import { extractLeadFields, hasMeaningfulValue } from '../utils/fieldMapping';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface AlticeResidencialFormProps {
  lead?: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    dif?: string;
    section?: string;
  };
  onClose?: () => void;
  onSave?: () => void;
}

interface AlticeResidencialFormData {
  // Client Identification
  tipo_cliente: string;
  numero_servico_atual?: string;
  segmento: string;
  categoria: string;
  tecnologia: string;
  tipologia: string;
  pacote_id: string;
  nome: string;
  contribuinte: string;
  cc_bi_passaporte: string;
  data_nascimento?: string;
  nacionalidade: string;
  morada_instalacao: string;
  coordenadas?: string;
  cod_postal_instalacao: string;
  localidade_instalacao: string;
  telefone_contacto: string;
  email?: string;

  // Account Administrator
  admin_conta_nome?: string;
  admin_conta_telefone?: string;
  admin_conta_telemovel?: string;
  admin_conta_email?: string;
  admin_conta_nif?: string;
  admin_conta_bi?: string;

  // Billing
  debito_direto: string;
  fatura_eletronica: string;
  telemovel_aviso_faturacao: string;
  IBAN?: string;
  nome_titular_conta_bancaria?: string;

  // Services
  televisao: string;
  televisao_num_canais?: string;
  primeira_meo_box?: string;
  internet_fixa: string;
  internet_fixa_velocidade_download?: string;
  internet_fixa_velocidade_upload?: string;
  internet_fixa_trafego_ilimitado?: string;
  internet_fixa_trafego_limitado_a?: string;
  telefone_fixo: string;
  telefone_fixo_tarifario?: string;
  telefone_fixo_equipamento?: string;
  portabilidade_fixa?: string;
  num_telefone_fixo?: string;
  operadora_fixo?: string;
  CVP_fixo?: string;
  telefone_movel: string;
  telefone_movel_num_cartoes?: string;
  telefone_movel_num_minutos?: string;
  telefone_movel_num_sms?: string;
  telefone_movel_num_gb?: string;
  internet_movel: string;
  internet_movel_plafond?: string;
  internet_movel_router?: string;
  internet_movel_router_imei?: string;

  // Package Details
  periodo_fidelizacao: string;
  preco: string;

  // Additional Services
  servicos_adicionais_num_box?: string;
  servicos_adicionais_tipo_box?: string;
  servicos_adicionais_box_valor?: string;
  servicos_adicionais_cartoes_telefone_movel?: string;
  servicos_adicionais_cartoes_telefone_movel_valor?: string;
  servicos_adicionais_gb_internet_movel?: string;
  servicos_adicionais_gb_internet_movel_valor?: string;
  servicos_adicionais_internet_movel_pc_tablet?: string;
  servicos_adicionais_internet_movel_pc_tablet_valor?: string;
  servicos_adicionais_equipamento_telefone_fixo?: string;
  servicos_adicionais_aquisicao_equipamento_telefone_fixo?: string;
  servicos_adicionais_equipamento_telefone_fixo_modalidade?: string;
  servicos_adicionais_equipamento_telefone_fixo_valor?: string;
  servicos_adicionais_outros?: string;
  servicos_adicionais_outros_valor?: string;
  servicos_adicionais_total?: string;

  // Equipment
  equipamentos_tipo_instalacao?: string;
  equipamentos_meobox_grav?: string;
  equipamentos_meobox_sem_grav?: string;
  equipamentos_box_4k?: string;
  equipamentos_regime_equipamentos?: string;
  equipamentos_quant_caixas_tdt?: string;
  equipamentos_serial_number?: string;
  equipamentos_card?: string;
  equipamentos_serial_number2?: string;
  equipamentos_card2?: string;
  equipamentos_serial_number3?: string;
  equipamentos_card3?: string;

  // Offers and Discounts
  oferta_desconto_instalacao_ativacao?: string;
  oferta_servico_meo_go?: string;
  desconto_incluido_media_fidelizacao?: string;
  outras_ofertas?: string;

  // Mobile Phones
  telefone_movel1_numero?: string;
  telefone_movel1_modalidade?: string;
  telefone_movel1_mesmo_titular?: string;
  telefone_movel1_nome_utilizador?: string;
  telefone_movel1_contribuinte?: string;
  telefone_movel1_cc_bi_passaporte?: string;
  telefone_movel1_portabilidade?: string;
  telefone_movel1_operadora?: string;
  telefone_movel1_CVP?: string;

  telefone_movel2_numero?: string;
  telefone_movel2_modalidade?: string;
  telefone_movel2_mesmo_titular?: string;
  telefone_movel2_nome_utilizador?: string;
  telefone_movel2_contribuinte?: string;
  telefone_movel2_cc_bi_passaporte?: string;
  telefone_movel2_portabilidade?: string;
  telefone_movel2_operadora?: string;
  telefone_movel2_CVP?: string;

  telefone_movel3_numero?: string;
  telefone_movel3_modalidade?: string;
  telefone_movel3_mesmo_titular?: string;
  telefone_movel3_nome_utilizador?: string;
  telefone_movel3_contribuinte?: string;
  telefone_movel3_cc_bi_passaporte?: string;
  telefone_movel3_portabilidade?: string;
  telefone_movel3_operadora?: string;
  telefone_movel3_CVP?: string;

  telefone_movel4_numero?: string;
  telefone_movel4_modalidade?: string;
  telefone_movel4_mesmo_titular?: string;
  telefone_movel4_nome_utilizador?: string;
  telefone_movel4_contribuinte?: string;
  telefone_movel4_cc_bi_passaporte?: string;
  telefone_movel4_portabilidade?: string;
  telefone_movel4_operadora?: string;
  telefone_movel4_CVP?: string;

  telefone_movel5_numero?: string;
  telefone_movel5_modalidade?: string;
  telefone_movel5_mesmo_titular?: string;
  telefone_movel5_nome_utilizador?: string;
  telefone_movel5_contribuinte?: string;
  telefone_movel5_cc_bi_passaporte?: string;
  telefone_movel5_portabilidade?: string;
  telefone_movel5_operadora?: string;
  telefone_movel5_CVP?: string;

  // Data Processing
  autoriza_tratamento_dados_comunicacoes: string;

  // Observations
  observacoes?: string;
  data_preferencial_agendamento?: string;
  horario_preferencial_agendamento?: string;

  // MEO Energy
  morada_ponto_entrega?: string;
  morada_ponto_entrega_cp7?: string;
  morada_ponto_entrega_localidade?: string;
  CPE?: string;
  alteracao_titular?: string;
  potencia_contratada?: string;
  ciclo?: string;
  tarifa?: string;
  fornecedor_atual?: string;
  tarifa_social?: string;

  // Documentation
  documentacao?: UploadFile[];
}

const AlticeResidencialForm: React.FC<AlticeResidencialFormProps> = ({ lead, onClose, onSave }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showAdditionalServices, setShowAdditionalServices] = useState(false);
  const [showOffersDiscounts, setShowOffersDiscounts] = useState(false);
  const [showDataProcessing, setShowDataProcessing] = useState(false);
  const [showAccountAdmin, setShowAccountAdmin] = useState(false);
  const [showMEOEnergy, setShowMEOEnergy] = useState(false);

  const [loading, setLoading] = useState(false);

  // Pre-fill form when component mounts
  useEffect(() => {
    if (lead?.id) {
      void prefillFormWithLeadData();
    }
  }, [lead?.id]);

  const firstStored = (bag: Record<string, any>, keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = bag[k];
      if (hasMeaningfulValue(v)) return String(v);
    }
    return undefined;
  };

  const prefillFormWithLeadData = async () => {
    if (!lead) return;

    const leadFields = extractLeadFields(lead);
    if (!leadFields) return;

    let bag: Record<string, any> = {};
    if (lead.id) {
      try {
        const res = await apiService.getFieldValues(lead.id);
        if (res.success && res.field_values) bag = res.field_values;
      } catch {
        /* ignore */
      }
    }

    const nome =
      firstStored(bag, ['nome', 'Nome', 'Nome Completo', 'Nome do Cliente', 'Novo Titular', 'nomeRepresentante']) ??
      leadFields.name ??
      '';
    const contrib =
      firstStored(bag, ['contribuinte', 'NIF', 'nif', 'Contribuinte', 'NIF Novo Titular', 'Num_Doc_Identificação_1']) ??
      leadFields.nif ??
      '';
    const email =
      firstStored(bag, ['email', 'Email', 'emailRepresentante', 'admin_conta_email']) ?? leadFields.email ?? '';
    const phone =
      firstStored(bag, [
        'telefone_contacto',
        'Telefone Contacto',
        'Contacto',
        'Telefone',
        'Telemóvel',
        'Telemóvel de Contacto',
        'admin_conta_telefone',
        'admin_conta_telemovel',
      ]) ?? leadFields.phone ?? '';
    const morada =
      firstStored(bag, ['morada_instalacao', 'Morada', 'Morada Completa', 'Endereço', 'moradaInstalacao']) ??
      leadFields.address ??
      '';
    const local =
      firstStored(bag, ['localidade_instalacao', 'Cidade', 'Localidade', 'localidade_instalacao']) ??
      leadFields.city ??
      '';
    const cp =
      firstStored(bag, ['cod_postal_instalacao', 'Código Postal', 'cod_postal_instalacao', 'postal_code']) ??
      leadFields.postalCode ??
      '';

    form.setFieldsValue({
      nome,
      contribuinte: contrib,
      email,
      telefone_contacto: phone,
      morada_instalacao: morada,
      localidade_instalacao: local,
      cod_postal_instalacao: cp,
      tipo_cliente: 'Novo Cliente',
      segmento: 'Consumo',
      categoria: 'ACRÉSCIMO CARTÃO',
      tecnologia: 'Fibra',
      debito_direto: 'Não',
      fatura_eletronica: 'Não',
      periodo_fidelizacao: '24',
      admin_conta_nome: firstStored(bag, ['admin_conta_nome', 'Nome', 'Nome Completo']) ?? leadFields.name ?? '',
      admin_conta_email: firstStored(bag, ['admin_conta_email', 'Email']) ?? leadFields.email ?? '',
      admin_conta_telefone: firstStored(bag, ['admin_conta_telefone', 'Telefone Contacto']) ?? leadFields.phone ?? '',
      admin_conta_telemovel: firstStored(bag, ['admin_conta_telemovel', 'Telemóvel']) ?? leadFields.phone ?? '',
      admin_conta_nif: firstStored(bag, ['admin_conta_nif', 'NIF', 'nif']) ?? leadFields.nif ?? '',
      telemovel_aviso_faturacao: phone,
      nome_titular_conta_bancaria: nome,
    });
  };


  const onFinish = async (values: AlticeResidencialFormData) => {
    try {
      setLoading(true);
      
      // Prepare the data for submission
      const { documentacao: _docs, ...saleValues } = values;
      const formData: Record<string, any> = {
        ...saleValues,
        lead_id: lead?.id,
        data_nascimento: values.data_nascimento
          ? (values.data_nascimento as any).format?.('YYYY-MM-DD') || values.data_nascimento
          : undefined,
        data_preferencial_agendamento: values.data_preferencial_agendamento
          ? (values.data_preferencial_agendamento as any).format?.('YYYY-MM-DD') ||
            values.data_preferencial_agendamento
          : undefined,
      };

      Object.keys(formData).forEach((key) => {
        if (formData[key] === undefined || formData[key] === null) {
          delete formData[key];
        }
      });

      const result = await apiService.createAlticeSale(formData);
      const saleId = result?.id;

      if (lead?.id && fileList.length > 0 && saleId) {
        let uploaded = 0;
        for (let i = 0; i < fileList.length; i++) {
          const item = fileList[i];
          const file = item.originFileObj as File | undefined;
          if (!file) continue;
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('stage', lead.section || 'meo');
          uploadData.append('field_name', `altice_sale_${saleId}_doc_${i}`.slice(0, 100));
          try {
            const uploadResp = await apiService.uploadLeadFile(lead.id, uploadData);
            if (uploadResp.success) uploaded += 1;
          } catch {
            /* continue with remaining files */
          }
        }
        if (uploaded > 0 && uploaded < fileList.length) {
          message.warning('Venda guardada, mas alguns anexos não foram carregados.');
        }
      }

      message.success('Venda Altice salva com sucesso!');
      onSave?.();
      onClose?.();
      
    } catch (error: unknown) {
      let errorMessage = 'Erro desconhecido';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        // Handle axios error response
        const axiosError = error as any;
        if (axiosError.response && axiosError.response.data) {
          errorMessage = axiosError.response.data.error || axiosError.response.data.message || 'Erro na resposta do servidor';
        } else {
          errorMessage = axiosError.message || 'Erro de conexão';
        }
      }
      
      message.error(`Erro ao salvar a venda: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTipoClienteChange = (value: string) => {
    if (value === 'Atual Cliente') {
      setShowAccountAdmin(true);
    } else {
      setShowAccountAdmin(false);
    }
  };

  const handleSegmentoChange = (value: string) => {
    if (value === 'Empresarial') {
      setShowMEOEnergy(true);
    } else {
      setShowMEOEnergy(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          tipo_cliente: '',
          segmento: '',
          categoria: '',
          tecnologia: '',
          tipologia: '',
          pacote_id: '',
          debito_direto: '',
          fatura_eletronica: '',
          televisao: '',
          internet_fixa: '',
          telefone_fixo: '',
          telefone_movel: '',
          internet_movel: '',
          periodo_fidelizacao: '24',
          autoriza_tratamento_dados_comunicacoes: 'Sim'
        }}
      >
        {/* Client Identification Section */}
        <Card title="Identificação do Cliente" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="tipo_cliente"
                label="Tipo de Cliente"
              >
                <Select onChange={handleTipoClienteChange}>
                  <Option value="">Selecione...</Option>
                  <Option value="Novo Cliente">Novo Cliente</Option>
                  <Option value="Atual Cliente">Atual Cliente</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="numero_servico_atual"
                label="Número de serviço atual"
                style={{ display: form.getFieldValue('tipo_cliente') === 'Atual Cliente' ? 'block' : 'none' }}
              >
                <Input maxLength={10} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="segmento"
                label="Segmento"
              >
                <Select onChange={handleSegmentoChange}>
                  <Option value="">Selecione...</Option>
                  <Option value="Consumo">Consumo</Option>
                  <Option value="Empresarial">Empresarial</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="categoria"
                label="Categoria"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value=" NOVO SERVIÇO"> NOVO SERVIÇO</Option>
                  <Option value="ACRÉSCIMO CARTÃO">ACRÉSCIMO CARTÃO</Option>
                  <Option value="ALTERAÇÃO DE PACOTE">ALTERAÇÃO DE PACOTE</Option>
                  <Option value="ALTERAÇÃO TECNOLOGIA">ALTERAÇÃO TECNOLOGIA</Option>
                  <Option value="MUDANÇA DE CASA">MUDANÇA DE CASA</Option>
                  <Option value="Pré-Venda">Pré-Venda</Option>
                  <Option value="Resub Port Móvel">Resub Port Móvel</Option>
                  <Option value="SERVIÇOS ADICIONAIS">SERVIÇOS ADICIONAIS</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="tecnologia"
                label="Tecnologia"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Fibra">Fibra</Option>
                  <Option value="Cabo">Cabo</Option>
                  <Option value="Satélite">Satélite</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="tipologia"
                label="Tipologia"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="T1">T1</Option>
                  <Option value="T2">T2</Option>
                  <Option value="T3">T3</Option>
                  <Option value="T4">T4</Option>
                  <Option value="T5">T5</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pacote_id"
                label="Pacote"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="pacote1">Pacote Básico</Option>
                  <Option value="pacote2">Pacote Standard</Option>
                  <Option value="pacote3">Pacote Premium</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="nome"
                label="Nome do Cliente"
              >
                <Input maxLength={256} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="contribuinte"
                label="Contribuinte"
              >
                <Input maxLength={9} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="cc_bi_passaporte"
                label="C.Cidadão/BI/Passaporte"
              >
                <Input maxLength={11} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="data_nascimento"
                label="Data Nascimento"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="nacionalidade"
                label="Nacionalidade"
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="morada_instalacao"
                label="Morada de instalação"
              >
                <Input maxLength={256} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="coordenadas"
                label="Coordenadas Google Maps"
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="cod_postal_instalacao"
                label="Cód. Postal"
              >
                <Input maxLength={8} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="localidade_instalacao"
                label="Localidade"
              >
                <Input maxLength={80} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="telefone_contacto"
                label="Telefone Contacto"
              >
                <Input maxLength={9} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input maxLength={256} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Account Administrator Section */}
        {showAccountAdmin && (
          <Card title="Administrador de Conta" style={{ marginBottom: '24px' }}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="admin_conta_nome"
                  label="Nome"
                >
                  <Input maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="admin_conta_telefone"
                  label="Telefone"
                >
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="admin_conta_telemovel"
                  label="Telemóvel"
                >
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="admin_conta_email"
                  label="e-mail"
                >
                  <Input maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="admin_conta_nif"
                  label="Doc. Fiscal (NIF)"
                >
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="admin_conta_bi"
                  label="Doc. Pessoal (BI)"
                >
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Billing Section */}
        <Card title="Fatura" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="debito_direto"
                label="Débito Direto"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Não">Não</Option>
                  <Option value="Sim">Sim</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="fatura_eletronica"
                label="Fatura Eletrónica"
              >
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Não">Não</Option>
                  <Option value="Sim">Sim</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="telemovel_aviso_faturacao"
                label="Telemóvel avisos faturação"
              >
                <Input maxLength={9} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="IBAN"
                label="IBAN"
              >
                <Input maxLength={35} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nome_titular_conta_bancaria"
                label="Nome do titular"
                style={{ display: form.getFieldValue('debito_direto') === 'Sim' ? 'block' : 'none' }}
              >
                <Input maxLength={256} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Tariff/Package Section */}
        <Card title="Tarifário / Pacote" style={{ marginBottom: '24px' }}>
          <Card title="DESCRIÇÃO DO(S) SERVIÇO(S)" size="small" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={9}>
                {/* Television Service */}
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="televisao" label="Televisão">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="televisao_num_canais" label="nº canais">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="primeira_meo_box" label="1ª MEOBox">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="4K">4K</Option>
                          <Option value="DVR">DVR</Option>
                          <Option value="Full HD">Full HD</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Fixed Internet Service */}
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="internet_fixa" label="Internet Fixa">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_fixa_velocidade_download" label="Velocidade Download">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_fixa_velocidade_upload" label="Velocidade Upload">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_fixa_trafego_ilimitado" label="Tráfego Ilimitado">
                        <Select>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="internet_fixa_trafego_limitado_a" label="Tráfego Incluído">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Fixed Phone Service */}
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="telefone_fixo" label="Telefone Fixo">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="telefone_fixo_tarifario" label="Tarifário">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="telefone_fixo_equipamento" label="Equipamento incluído">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Sim">Sim</Option>
                          <Option value="Não">Não</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="portabilidade_fixa" label="Portabilidade Nº Fixo">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="num_telefone_fixo" label="Nº Telefone Fixo">
                        <Input maxLength={9} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="operadora_fixo" label="Operador Doador">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="CaboVisão">CaboVisão</Option>
                          <Option value="Vodafone">Vodafone</Option>
                          <Option value="NOS Comunicações SA (Ex ZON)">NOS Comunicações SA (Ex ZON)</Option>
                          <Option value="NOS Comunicações SA (Ex Optimus)">NOS Comunicações SA (Ex Optimus)</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="CVP_fixo" label="CVP Nº Fixo">
                        <Input maxLength={12} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Mobile Phone Service */}
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="telefone_movel" label="Telefone Móvel">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="telefone_movel_num_cartoes" label="Nº Cartões">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name="telefone_movel_num_minutos" label="Nº Minutos">
                        <Input maxLength={30} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name="telefone_movel_num_sms" label="Nº SMS">
                        <Input maxLength={30} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name="telefone_movel_num_gb" label="GB de internet">
                        <Input maxLength={30} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Mobile Internet Service */}
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="internet_movel" label="Internet Móvel">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Não">Não</Option>
                          <Option value="Sim">Sim</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_movel_plafond" label="Plafond mensal">
                        <Input maxLength={30} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_movel_router" label="Router">
                        <Select>
                          <Option value="">Selecione...</Option>
                          <Option value="Sim">Sim</Option>
                          <Option value="Não">Não</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="internet_movel_router_imei" label="IMEI do Equipamento">
                        <Input maxLength={20} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Package Values */}
              <Col span={3}>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="periodo_fidelizacao" label="Fidelização">
                        <Select>
                          <Option value="24">24 meses</Option>
                          <Option value="12">12 meses</Option>
                          <Option value="0">0 meses</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="preco" label="Preço">
                        <Input maxLength={8} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Card>
        </Card>

        {/* Additional Services Section */}
        <Card title="SERVIÇOS / EQUIPAMENTOS ADICIONAIS" style={{ marginBottom: '24px' }}>
          <Button 
            type="dashed" 
            onClick={() => setShowAdditionalServices(!showAdditionalServices)}
            style={{ marginBottom: '16px' }}
          >
            {showAdditionalServices ? 'Ocultar' : 'Ver mais'}
          </Button>

          {showAdditionalServices && (
            <>
              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_num_box" label="Nº de Box Adicionais">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="1">1</Option>
                      <Option value="2">2</Option>
                      <Option value="3">3</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="servicos_adicionais_tipo_box" label="Tipo de Box Adicional">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="4K">4K</Option>
                      <Option value="DVR">DVR</Option>
                      <Option value="Full HD">Full HD</Option>
                      <Option value="Android TV">Android TV</Option>
                      <Option value="Apple TV">Apple TV</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_box_valor" label="Valor">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_cartoes_telefone_movel" label="Nº cartões tel adicionais">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="1">1 Cartão</Option>
                      <Option value="2">2 Cartões</Option>
                      <Option value="3">3 Cartões</Option>
                      <Option value="4">4 Cartões</Option>
                      <Option value="5">5 Cartões</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_cartoes_telefone_movel_valor" label="Valor">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_gb_internet_movel" label="GB Internet Móvel">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_gb_internet_movel_valor" label="Valor">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_internet_movel_pc_tablet" label="Internet Móvel PC/Tablet">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_internet_movel_pc_tablet_valor" label="Valor">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_equipamento_telefone_fixo" label="Equipamento Telefone Fixo">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Sim">Sim</Option>
                      <Option value="Não">Não</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_aquisicao_equipamento_telefone_fixo" label="Valor de compra">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_equipamento_telefone_fixo_modalidade" label="Modalidade">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Aluguer">Aluguer</Option>
                      <Option value="Compra">Compra</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_equipamento_telefone_fixo_valor" label="Valor">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_outros" label="Outros">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_outros_valor" label="Valor">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="servicos_adicionais_total" label="Total">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Card>

        {/* Equipment Section */}
        <Card title="Equipamentos" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="equipamentos_tipo_instalacao" label="Tipo de Instalação">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Auto Instalação">Auto Instalação</Option>
                  <Option value="Instalação assistida com OT">Instalação a efetuar pelo Tecnico da PT</Option>
                  <Option value="Instalação assistida sem OT">Instalação a efetuar pelo Parceiro</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_meobox_grav" label="Box C/Grav.">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="0">0</Option>
                  <Option value="1">1</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_meobox_sem_grav" label="Box S/Grav.">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="0">0</Option>
                  <Option value="1">1</Option>
                  <Option value="2">2</Option>
                  <Option value="3">3</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_box_4k" label="Box 4K">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="0">0</Option>
                  <Option value="1">1</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="equipamentos_regime_equipamentos" label="Regime">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Aluguer">Aluguer</Option>
                  <Option value="Compra">Compra</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_quant_caixas_tdt" label="Box TDT">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="0">0</Option>
                  <Option value="1">1</Option>
                  <Option value="2">2</Option>
                  <Option value="3">3</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="equipamentos_serial_number" label="Serial Number">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_card" label="Card">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_serial_number2" label="Serial Number 2">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_card2" label="Card 2">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="equipamentos_serial_number3" label="Serial Number 3">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="equipamentos_card3" label="Card 3">
                <Input maxLength={12} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Offers and Discounts Section */}
        <Card title="OFERTAS E DESCONTOS" style={{ marginBottom: '24px' }}>
          <Button 
            type="dashed" 
            onClick={() => setShowOffersDiscounts(!showOffersDiscounts)}
            style={{ marginBottom: '16px' }}
          >
            {showOffersDiscounts ? 'Ocultar' : 'Ver mais'}
          </Button>

          {showOffersDiscounts && (
            <Card title="OFERTAS E/OU DESCONTOS (VANTAGENS) ASSOCIADOS À FIDELIZAÇÃO" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="oferta_desconto_instalacao_ativacao" label="Oferta/desconto na instalação e ativação">
                    <Select>
                      <Option value="Não">Não</Option>
                      <Option value="Sim">Sim</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="oferta_servico_meo_go" label="Oferta serviço MEO Go">
                    <Select>
                      <Option value="Não">Não</Option>
                      <Option value="Sim">Sim</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="desconto_incluido_media_fidelizacao" label="Desconto incluído no preço mediante fidelização">
                    <Input maxLength={128} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="outras_ofertas" label="Outras Ofertas">
                    <Input maxLength={128} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}
        </Card>

        {/* Mobile Phone Section */}
        <Card title="TELEFONE MÓVEL" style={{ marginBottom: '24px' }}>
          {/* Card 1 */}
          <Card title="Cartão 1" size="small" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item name="telefone_movel1_numero" label="Nº Telef Móvel 1">
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_modalidade" label="Modalidade">
                  <Select>
                    <Option value="">Selecione...</Option>
                    <Option value="Novo">Novo</Option>
                    <Option value="Pré-Pago">Pré-Pago</Option>
                    <Option value="Pós-Pago">Pós-Pago</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_mesmo_titular" label="Mesmo Titular">
                  <Select>
                    <Option value="">Selecione...</Option>
                    <Option value="Sim">Sim</Option>
                    <Option value="Não">Não</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_nome_utilizador" label="Nome do utilizador">
                  <Input maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_contribuinte" label="Contribuinte">
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_cc_bi_passaporte" label="C.Cidadão/BI/Passaporte">
                  <Input maxLength={11} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={4}>
                <Form.Item name="telefone_movel1_portabilidade" label="Portabilidade">
                  <Select>
                    <Option value="">Selecione...</Option>
                    <Option value="Não">Não</Option>
                    <Option value="Sim">Sim</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_operadora" label="Operador Atual">
                  <Select>
                    <Option value="">Selecione...</Option>
                    <Option value="Vodafone">Vodafone</Option>
                    <Option value="NOS Comunicações SA (Ex Zon)">NOS Comunicações SA (Ex Zon)</Option>
                    <Option value="CTT">CTT</Option>
                    <Option value="Lycamobile">Lycamobile</Option>
                    <Option value="MEO">MEO</Option>
                    <Option value="Nowo">Nowo</Option>
                    <Option value="Nenhum">Nenhum</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="telefone_movel1_CVP" label="CVP">
                  <Input maxLength={12} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Cards 2-5 would follow the same pattern */}
        </Card>

        {/* Data Processing Section */}
        <Card title="TRATAMENTO E COMUNICAÇÃO DE DADOS PESSOAIS" style={{ marginBottom: '24px' }}>
          <Button 
            type="dashed" 
            onClick={() => setShowDataProcessing(!showDataProcessing)}
            style={{ marginBottom: '16px' }}
          >
            {showDataProcessing ? 'Ocultar' : 'Ver mais'}
          </Button>

          {showDataProcessing && (
            <Row>
              <Col span={24}>
                <Form.Item
                  name="autoriza_tratamento_dados_comunicacoes"
                  label="Autoriza o tratamento dos seus dados para efeito de comunicações de marketing da MEO?"
                >
                  <Radio.Group>
                    <Radio value="Sim">Sim</Radio>
                    <Radio value="Não">Não</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Card>

        {/* Observations Section */}
        <Card title="OBSERVAÇÕES" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="observacoes" label="Observações">
                <TextArea rows={5} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="data_preferencial_agendamento" label="Data Preferencial Agendamento/Fornecimento">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="horario_preferencial_agendamento" label="Horário Preferencial Agendamento">
                <Select>
                  <Option value="">Selecione...</Option>
                  <Option value="Manhã">Manhã</Option>
                  <Option value="Tarde">Tarde</Option>
                  <Option value="Todo o dia">Todo o dia</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* MEO Energy Section */}
        {showMEOEnergy && (
          <Card title="MEO Energia" style={{ marginBottom: '24px' }}>
            <Card title="Morada do Ponto de Entrega" size="small" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="morada_ponto_entrega" label="Morada de Ponto de Entrega">
                    <Input maxLength={255} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="morada_ponto_entrega_cp7" label="Código Postal">
                    <Input maxLength={8} />
                  </Form.Item>
                </Col>
                <Col span={18}>
                  <Form.Item name="morada_ponto_entrega_localidade" label="Localidade">
                    <Input maxLength={128} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Dados do Ponto de Entrega" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="CPE" label="CPE">
                    <Input maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="alteracao_titular" label="Alteração de Titular">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Sim">Sim</Option>
                      <Option value="Não">Não</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="potencia_contratada" label="Potencia Contratada">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="1.15">1.15</Option>
                      <Option value="2.30">2.30</Option>
                      <Option value="3.45">3.45</Option>
                      <Option value="4.60">4.60</Option>
                      <Option value="5.75">5.75</Option>
                      <Option value="6.90">6.90</Option>
                      <Option value="10.35">10.35</Option>
                      <Option value="13.80">13.80</Option>
                      <Option value="17.25">17.25</Option>
                      <Option value="20.70">20.70</Option>
                      <Option value="27.60">27.60</Option>
                      <Option value="34.50">34.50</Option>
                      <Option value="41.40">41.40</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="ciclo" label="Ciclo">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Diário">Diário</Option>
                      <Option value="Semanal">Semanal</Option>
                      <Option value="Sem Ciclo">Sem Ciclo</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="tarifa" label="Tarifa">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Simples">Simples</Option>
                      <Option value="Bi-Horário">Bi-Horário</Option>
                      <Option value="Tri-Horário">Tri-Horário</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="fornecedor_atual" label="Forncedor Atual">
                    <Input maxLength={128} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="tarifa_social" label="Tarifa Social">
                    <Select>
                      <Option value="">Selecione...</Option>
                      <Option value="Não">Não</Option>
                      <Option value="Sim">Sim</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Card>
        )}

        {/* Documentation Section */}
        <Card title="Documentação da Venda" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="documentacao" label="Documentação">
                <Upload
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  beforeUpload={() => false}
                  multiple
                >
                  <Button icon={<UploadOutlined />}>Carregar Documentos</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Button type="primary" htmlType="submit" size="large" loading={loading}>
            Inserir Venda
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AlticeResidencialForm; 